import { EditOutlined, EyeOutlined, PlusOutlined } from '@ant-design/icons';
import {
  App,
  Button,
  Form,
  Input,
  Modal,
  Space,
  Table,
  Tag,
  Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  PROJECT_REPORT_STATUS_LABELS,
  projectReportStatusTagColor,
} from '@/constants/project-report';
import { ProjectReportDetailDrawer } from '@/pages/admin/project-reports/ProjectReportDetailDrawer';
import { ApiError } from '@/services/api/client';
import * as projectReportsApi from '@/services/api/project-reports';
import type { ProjectReportRecord } from '@/types/project-report';
import { formatProjectReportDate } from '@/utils/format-date';

interface ProjectProgressTimelineSectionProps {
  projectId: string;
  projectName: string;
  editable: boolean;
  onChanged?: () => void;
}

interface ProjectProgressFormValues {
  progressContent: string;
  problemContent?: string;
  nextPlanContent?: string;
}

interface WorkWeek {
  startKey: string;
  endKey: string;
  todayKey: string;
  label: string;
}

function getCurrentWorkWeek(): WorkWeek {
  const current = dayjs().startOf('day');
  const weekday = current.day();
  const offsetToMonday = weekday === 0 ? -6 : 1 - weekday;
  const start = current.add(offsetToMonday, 'day');
  const end = start.add(4, 'day');
  const startKey = start.format('YYYY-MM-DD');
  const endKey = end.format('YYYY-MM-DD');
  return {
    startKey,
    endKey,
    todayKey: current.format('YYYY-MM-DD'),
    label: `${startKey} 至 ${endKey}`,
  };
}

function renderFullText(text: string | null | undefined) {
  const value = (text || '').trim();
  return (
    <Typography.Paragraph
      style={{ marginBottom: 0, overflowWrap: 'anywhere', whiteSpace: 'pre-wrap' }}
    >
      {value || '—'}
    </Typography.Paragraph>
  );
}

export function ProjectProgressTimelineSection({
  projectId,
  projectName,
  editable,
  onChanged,
}: ProjectProgressTimelineSectionProps) {
  const { message } = App.useApp();
  const [form] = Form.useForm<ProjectProgressFormValues>();
  const [loading, setLoading] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [reports, setReports] = useState<ProjectReportRecord[]>([]);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailReportId, setDetailReportId] = useState<string | null>(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingReportId, setEditingReportId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const currentWeek = useMemo(() => getCurrentWorkWeek(), []);

  const currentWeekReport = useMemo(
    () =>
      reports.find(
        (report) =>
          report.reportDate >= currentWeek.startKey && report.reportDate <= currentWeek.endKey,
      ) ?? null,
    [currentWeek.endKey, currentWeek.startKey, reports],
  );

  const loadReports = useCallback(async () => {
    setLoading(true);
    setLoadFailed(false);
    try {
      const result = await projectReportsApi.fetchProjectReportsByProject(projectId, {
        page: 1,
        pageSize: 100,
        reportPeriod: 'WEEKLY',
      });
      setReports(result.items);
    } catch (error) {
      const text = error instanceof ApiError ? error.message : '加载项目进展失败';
      message.error(text);
      setReports([]);
      setLoadFailed(true);
    } finally {
      setLoading(false);
    }
  }, [message, projectId]);

  useEffect(() => {
    void loadReports();
  }, [loadReports]);

  const openEditor = () => {
    const report = currentWeekReport;
    setEditingReportId(report?.id ?? null);
    form.setFieldsValue({
      progressContent: report?.progressContent ?? '',
      problemContent: report?.problemContent ?? '',
      nextPlanContent: report?.nextPlanContent ?? '',
    });
    setEditorOpen(true);
  };

  const handleSave = async (values: ProjectProgressFormValues) => {
    const progressContent = values.progressContent.trim();
    const problemContent = values.problemContent?.trim() ?? '';
    const nextPlanContent = values.nextPlanContent?.trim() ?? '';

    setSaving(true);
    try {
      if (editingReportId) {
        await projectReportsApi.updateProjectReport(editingReportId, {
          progressContent,
          problemContent: problemContent || null,
          nextPlanContent: nextPlanContent || null,
        });
      } else {
        await projectReportsApi.createProjectReport({
          projectId,
          reportPeriod: 'WEEKLY',
          reportDate: currentWeek.todayKey,
          progressPercent: 0,
          progressContent,
          problemContent: problemContent || undefined,
          nextPlanContent: nextPlanContent || undefined,
        });
      }

      message.success(editingReportId ? '本周项目进展已更新' : '本周项目进展已提交');
      setEditorOpen(false);
      setEditingReportId(null);
      form.resetFields();
      await loadReports();
      onChanged?.();
    } catch (error) {
      if (error instanceof ApiError && error.status === 409) {
        message.warning('本周项目进展已存在，列表已刷新，请重新打开修改');
        setEditorOpen(false);
        setEditingReportId(null);
        form.resetFields();
        await loadReports();
      } else {
        const text = error instanceof ApiError ? error.message : '保存项目进展失败';
        message.error(text);
      }
    } finally {
      setSaving(false);
    }
  };

  const columns: ColumnsType<ProjectReportRecord> = [
    {
      title: '填报日期',
      dataIndex: 'reportDate',
      width: 120,
      render: (_: string, record) =>
        formatProjectReportDate(record.reportDate, record.createdAt, record.reportPeriod),
    },
    {
      title: '填报人',
      dataIndex: ['createdBy', 'name'],
      width: 100,
    },
    {
      title: '本周进展',
      dataIndex: 'progressContent',
      render: (value: string) => renderFullText(value),
    },
    {
      title: '存在问题',
      dataIndex: 'problemContent',
      width: 220,
      render: (value: string | null) => renderFullText(value),
    },
    {
      title: '状态',
      dataIndex: 'status',
      width: 100,
      render: (status: ProjectReportRecord['status']) => (
        <Tag color={projectReportStatusTagColor(status)}>
          {PROJECT_REPORT_STATUS_LABELS[status]}
        </Tag>
      ),
    },
    {
      title: '操作',
      key: 'actions',
      width: 90,
      render: (_, record) => (
        <Button
          type="link"
          icon={<EyeOutlined />}
          onClick={() => {
            setDetailReportId(record.id);
            setDetailOpen(true);
          }}
        >
          查看
        </Button>
      ),
    },
  ];

  return (
    <>
      <div
        style={{
          alignItems: 'flex-start',
          display: 'flex',
          gap: 16,
          justifyContent: 'space-between',
          marginBottom: 12,
        }}
      >
        <Typography.Paragraph type="secondary" style={{ marginBottom: 0 }}>
          项目负责人和管理员填写的周进展使用同一份记录，最新一条同步展示在项目台账中。
        </Typography.Paragraph>
        {editable ? (
          <Button
            type="primary"
            icon={currentWeekReport ? <EditOutlined /> : <PlusOutlined />}
            disabled={loading || loadFailed}
            onClick={openEditor}
          >
            {currentWeekReport ? '修改本周进展' : '填写本周进展'}
          </Button>
        ) : (
          <Typography.Text type="secondary">归档项目不可修改</Typography.Text>
        )}
      </div>

      <Table
        rowKey="id"
        size="small"
        loading={loading}
        columns={columns}
        dataSource={reports}
        pagination={{ pageSize: 10, hideOnSinglePage: true, showSizeChanger: false }}
        locale={{ emptyText: '暂无项目周报' }}
        scroll={{ x: 900 }}
      />

      <Modal
        title={editingReportId ? '修改本周项目进展' : '填写本周项目进展'}
        open={editorOpen}
        confirmLoading={saving}
        okText={editingReportId ? '保存修改' : '提交进展'}
        cancelText="取消"
        onCancel={() => {
          setEditorOpen(false);
          setEditingReportId(null);
          form.resetFields();
        }}
        onOk={() => form.submit()}
        destroyOnClose
      >
        <Space direction="vertical" size={2} style={{ marginBottom: 16 }}>
          <Typography.Text strong>{projectName}</Typography.Text>
          <Typography.Text type="secondary">本周周期：{currentWeek.label}</Typography.Text>
          <Typography.Text type="secondary">填报日期：{currentWeek.todayKey}</Typography.Text>
        </Space>
        <Form form={form} layout="vertical" onFinish={(values) => void handleSave(values)}>
          <Form.Item
            name="progressContent"
            label="本周进展"
            rules={[
              { required: true, whitespace: true, message: '请填写本周进展' },
              { max: 10000, message: '本周进展不能超过 10000 字' },
            ]}
          >
            <Input.TextArea
              rows={5}
              maxLength={10000}
              showCount
              placeholder="填写本周完成事项和关键节点进展"
            />
          </Form.Item>
          <Form.Item name="problemContent" label="存在问题" rules={[{ max: 10000 }]}>
            <Input.TextArea
              rows={3}
              maxLength={10000}
              showCount
              placeholder="没有可不填"
            />
          </Form.Item>
          <Form.Item name="nextPlanContent" label="下周计划" rules={[{ max: 10000 }]}>
            <Input.TextArea
              rows={3}
              maxLength={10000}
              showCount
              placeholder="填写下一周计划，没有可不填"
            />
          </Form.Item>
        </Form>
      </Modal>

      <ProjectReportDetailDrawer
        open={detailOpen}
        reportId={detailReportId}
        onClose={() => {
          setDetailOpen(false);
          setDetailReportId(null);
        }}
        onChanged={() => {
          void loadReports();
          onChanged?.();
        }}
      />
    </>
  );
}
